import { CommonModule } from '@angular/common';
import { Component, OnInit, WritableSignal, signal, input, inject, InputSignal, computed } from '@angular/core';
import { MatSelectModule } from '@angular/material/select';
import { Router, RouterModule } from '@angular/router';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { SignalsStoreService } from '../../signals-store.service';
import { FormsModule } from '@angular/forms';
import { OrderService } from '../../order.service';
import { NumberRestrictionDirective } from '../../directives/number-restriction.directive';
import { NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { BundleEditionTypes } from '../../types/order.type';
import { finalize } from 'rxjs';
import { CartPreviewService } from '../../../cart-preview/cart-preview.service';
import { CalculationsService } from '../../calculations.service';
import { EmptyMessageComponent } from '../../empty-message/empty-message.component';
import { ResolutionService } from '../../resolution.service';

@Component({
  selector: 'app-product-cart-preview',
  templateUrl: './product-cart-preview.component.html',
  styleUrl: './product-cart-preview.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    NumberRestrictionDirective,
    NgbTooltip,
    EmptyMessageComponent
  ]
})

export class ProductCartPreview implements OnInit {
  #resolutionService = inject(ResolutionService);
  signalsStoreService = inject(SignalsStoreService)
  orderService = inject(OrderService)
  #router = inject(Router);
  #cartPreviewService = inject(CartPreviewService);
  #calculationsService = inject(CalculationsService);

  isInputValid!: boolean;
  previousValue!: number;
  selectedQuantity: WritableSignal<any> = signal(1);
  newQuantity: WritableSignal<any> = signal(1);

  product: any = input(null, {
    alias: 'product',
    transform: (product: any) => {
      this.previousValue = product.quantity;
      this.newQuantity.set(product.quantity)
      return product;
    }
  })

  productKey: any = input(null, {
    alias: 'productKey'
  })

  allowModification: InputSignal<boolean> = input(true);

  isJustAddedProductNotification: InputSignal<boolean> = input(false, {
    alias: 'isJustAddedProductNotification'
  });

  isInOrder = input(false);

  // DEV-11183: el tooltip del badge se abre con tap en móvil y con hover en escritorio.

  isMobile = computed(() => this.#resolutionService.isMobile());

  odooOrder = computed(() => this.orderService.odooOrder());
  isDeletingProduct = computed(() => this.#cartPreviewService.isDeletingProduct());

  productTotalPrice = computed(() => this.#calculationsService.calculateProductTotalPrice(this.product()));

  productOriginalTotalPrice = computed(() => this.#calculationsService.calculateProductGrossTotalPrice(this.product()));

  ngOnInit(): void {
    this.isInputValid = true;
  }

  private validateInput(inputValue: number): void {
    const regex = /^\d+$/;
    this.isInputValid = regex.test(inputValue.toString());
  }

  onKeyUp(event: any): void {
    const { value } = event?.target
    this.validateInput(+value);
    if (!this.isInputValid)
      return

    this.newQuantity.set(+value)
  }

  updateQuantity() {
    if (!this.newQuantity()) return;
    this.orderService.updateFirebaseProduct(this.productKey(), { ...this.product(), quantity: this.newQuantity() }, this.signalsStoreService.firebaseOrder());
  }

  removeCartProduct() {
    if (this.isDeletingProduct())
      return;

    this.product().isDeleting = true;
    this.#cartPreviewService.setIsDeletingProduct(true);

    if (this.allowModification()) {
      this.orderService.removeFirebaseProduct(this.productKey(), this.product(), this.signalsStoreService.firebaseOrder());
      if (this.product().lineId)
        this.#deleteFromOdoo();
      else
        this.#cartPreviewService.setIsDeletingProduct(false);
      return;
    }
    this.#deleteFromOdoo();
  }

  #deleteFromOdoo() {
    const odooOrder = this.odooOrder();
    if (!odooOrder?.id) return;
    this.orderService
      .deleteProductOrder(+odooOrder.id, this.product().lineId)
      .pipe(
        finalize(() => {
          this.#cartPreviewService.setIsDeletingProduct(false);
          this.product().isDeleting = false;
        })
      )
      .subscribe()
  }

  handleMinus() {
    if (this.newQuantity() <= 1) return;
    // DEV-11172: pre-order lines can only be increased, never taken below
    // the quantity already on the order.
    if (this.product()?.isPreOrder && this.newQuantity() <= (this.product()?.quantity || 1)) return;
    this.newQuantity.update((current) => current - 1);
    this.updateQuantity()
  }

  handlePlus() {
    this.newQuantity.update((current) => current + 1);
    this.updateQuantity();
  }

  handleImageError(event: Event) {
    (event.target as HTMLImageElement).src = 'assets/images/product-placeholder.webp';
  }

  goToCustomizeBox() {
    this.signalsStoreService.showCartPreview.set(false);
    this.#router.navigate([`/shop/custom-box/${this.productKey()}/${this.product().id}`]);
  }
}
